import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Search, Menu, X, User, LogOut, LayoutDashboard, Moon, Sun, Bookmark } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../lib/auth-context';
import { breakingAPI, categoriesAPI, subscriptionsAPI, publicAPI, i18nStringsAPI } from '../lib/api';
import { useEdition } from '../lib/edition-context';
import useHeadlineExperiment from '../lib/use-headline-experiment';
import { formatDisplayViews } from '../lib/view-count';

const RTL_LANGS = new Set(['ar', 'he', 'fa', 'ur', 'yi', 'dv', 'ku', 'ps']);

export function useRTL(lang) {
  React.useEffect(() => {
    if (lang && RTL_LANGS.has(lang)) {
      document.documentElement.setAttribute('dir', 'rtl');
      document.documentElement.setAttribute('lang', lang);
    } else {
      document.documentElement.setAttribute('dir', 'ltr');
      document.documentElement.setAttribute('lang', lang || 'en');
    }
    return () => { document.documentElement.setAttribute('dir', 'ltr'); };
  }, [lang]);
}
import { toast } from 'sonner';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

export function PublicHeader() {
  const { user, logout, isAdmin } = useAuth();
  const { edition, setEdition } = useEdition();
  const [categories, setCategories] = useState([]);
  const [editionsCatalog, setEditionsCatalog] = useState([]);
  const [breakingNews, setBreakingNews] = useState([]);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [darkMode, setDarkMode] = useState(() => {
    const stored = localStorage.getItem('theme');
    if (stored === 'dark') return true;
    if (stored === 'light') return false;
    if (typeof window !== 'undefined' && window.matchMedia) {
      return window.matchMedia('(prefers-color-scheme: dark)').matches;
    }
    return false;
  });

  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
  }, [darkMode]);

  useEffect(() => {
    categoriesAPI.list().then(r => setCategories(r.data.categories || [])).catch(() => {});
    breakingAPI.list(true).then(r => setBreakingNews(r.data.breaking_news || [])).catch(() => {});
    publicAPI
      .editions()
      .then((r) => {
        const list = r.data.editions || [];
        setEditionsCatalog(
          list.length ? list : [{ id: 'global', name: 'International', slug: 'global' }]
        );
      })
      .catch(() =>
        setEditionsCatalog([{ id: 'global', name: 'International', slug: 'global' }])
      );
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      window.location.href = `/search?q=${encodeURIComponent(searchQuery.trim())}`;
    }
  };

  return (
    <header className="sticky top-0 z-50">
      {/* Breaking News Ticker */}
      {breakingNews.length > 0 && (
        <div data-testid="breaking-news-banner" className="bg-[#121316] text-white border-b border-white/10">
          <div className="max-w-[1200px] mx-auto px-4 sm:px-6 flex items-center h-10 gap-3">
            <span className="breaking-label flex-shrink-0">
              <span className="w-2 h-2 bg-white rounded-full animate-pulse" />
              BREAKING
            </span>
            <div data-testid="breaking-news-ticker" className="overflow-hidden whitespace-nowrap flex-1">
              <div className="inline-block ticker-animate">
                {breakingNews.concat(breakingNews).map((item, i) => (
                  <span key={i} className="inline-block mr-12 text-sm">
                    {item.headline}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Header */}
      <div className="bg-[#FBF7EF] border-b border-[#E7E0D6]">
        {/* Top utility bar */}
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between h-8 text-xs text-[#5B616E] gap-2 flex-wrap">
            <span className="hidden sm:inline">{new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</span>
            {editionsCatalog.length > 0 && (
              <label className="flex items-center gap-1.5 shrink-0">
                <span className="text-[10px] uppercase tracking-wide text-[#5B616E]/80">Edition</span>
                <select
                  value={edition}
                  onChange={(e) => setEdition(e.target.value)}
                  data-testid="edition-select"
                  className="text-xs border border-[#E7E0D6] rounded-md px-2 py-0.5 bg-white text-[#121316] max-w-[140px]"
                >
                  {editionsCatalog.map((ed) => (
                    <option key={ed.slug || ed.id} value={ed.slug || ed.id}>
                      {ed.name || ed.slug}
                    </option>
                  ))}
                </select>
              </label>
            )}
            <div className="flex items-center gap-4 ml-auto">
              {user ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="flex items-center gap-1 hover:text-[#121316] transition-colors">
                      <User className="w-3 h-3" />
                      {user.full_name}
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {isAdmin && (
                      <DropdownMenuItem asChild>
                        <Link to="/admin"><LayoutDashboard className="w-4 h-4 mr-2" />Dashboard</Link>
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={logout}>
                      <LogOut className="w-4 h-4 mr-2" />Sign Out
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <>
                  <Link to="/login" data-testid="top-nav-login-link" className="hover:text-[#121316] dark:hover:text-white transition-colors">Sign In</Link>
                  <Link to="/register" className="hover:text-[#121316] dark:hover:text-white transition-colors">Subscribe</Link>
                </>
              )}
              <button
                onClick={() => setDarkMode(!darkMode)}
                className="hover:text-[#121316] dark:hover:text-white transition-colors flex items-center gap-1"
                data-testid="dark-mode-toggle"
                title={darkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
              >
                {darkMode ? <Sun className="w-3.5 h-3.5" /> : <Moon className="w-3.5 h-3.5" />}
              </button>
              {user && (
                <Link to="/bookmarks" className="hover:text-[#121316] dark:hover:text-white transition-colors flex items-center gap-1">
                  <Bookmark className="w-3 h-3" /> Saved
                </Link>
              )}
            </div>
          </div>
        </div>

        {/* Masthead */}
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <button className="lg:hidden" onClick={() => setMobileOpen(!mobileOpen)}>
              {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
            <Link to="/" className="news-masthead text-3xl sm:text-4xl font-bold text-[#121316] tracking-tight">
              Neural News
            </Link>
            <div className="flex items-center gap-3">
              <form onSubmit={handleSearch} className="flex items-center gap-2">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5B616E]" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    placeholder="Search news..."
                    className="border border-[#E7E0D6] rounded-lg pl-9 pr-3 py-1.5 text-sm w-48 focus:outline-none focus:ring-2 focus:ring-[#B89B5E] bg-white"
                    data-testid="masthead-search-input"
                  />
                </div>
                <Button type="submit" variant="ghost" size="sm" data-testid="masthead-search-button">
                  <Search className="w-4 h-4" />
                </Button>
              </form>
              <Link to="/register">
                <Button variant="default" size="sm" data-testid="masthead-subscribe-button" className="bg-[#121316] text-white hover:bg-[#1B1D22] hidden sm:inline-flex">
                  Subscribe
                </Button>
              </Link>
            </div>
          </div>
        </div>

        {/* Category Nav */}
        <div data-testid="category-nav" className="border-t border-[#E7E0D6]">
          <div className="max-w-[1200px] mx-auto px-4 sm:px-6">
            <nav className="flex items-center gap-1 overflow-x-auto py-2 scrollbar-hide" style={{ scrollbarWidth: 'none' }}>
              <Link
                to="/"
                className="news-kicker text-xs px-3 py-1.5 rounded-md hover:bg-[#E7E0D6] transition-colors whitespace-nowrap font-medium text-[#121316]"
              >
                Home
              </Link>
              {categories.map(cat => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.slug}`}
                  className="news-kicker text-xs px-3 py-1.5 rounded-md hover:bg-[#E7E0D6] transition-colors whitespace-nowrap text-[#5B616E] hover:text-[#121316]"
                >
                  {cat.name}
                </Link>
              ))}
            </nav>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="lg:hidden bg-white border-b border-[#E7E0D6] shadow-lg">
          <div className="max-w-[1200px] mx-auto px-4 py-4">
            <div className="flex flex-col gap-2">
              {categories.map(cat => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.slug}`}
                  className="py-2 px-3 text-sm hover:bg-[#FBF7EF] rounded-lg"
                  onClick={() => setMobileOpen(false)}
                >
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}

function NewsletterSignup() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [subscribed, setSubscribed] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !email.includes('@')) { toast.error('Please enter a valid email'); return; }
    setLoading(true);
    try {
      await subscriptionsAPI.subscribe({ email });
      setSubscribed(true);
      toast.success('Welcome! You\'re subscribed.');
    } catch (err) {
      if (err?.response?.status === 400) toast.info('You\'re already subscribed!');
      else toast.error('Failed to subscribe');
    }
    finally { setLoading(false); }
  };

  if (subscribed) {
    return <p className="text-xs text-[#B89B5E]">✓ Subscribed! Check your inbox.</p>;
  }

  return (
    <form className="flex gap-2" onSubmit={handleSubmit}>
      <input
        type="email"
        value={email}
        onChange={e => setEmail(e.target.value)}
        placeholder="your@email.com"
        required
        className="flex-1 bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#B89B5E] placeholder:text-white/30"
      />
      <Button size="sm" className="bg-[#B89B5E] text-white hover:bg-[#A08A50]" disabled={loading}>
        {loading ? '...' : 'Join'}
      </Button>
    </form>
  );
}

export function PublicFooter() {
  return (
    <footer className="bg-[#121316] text-white mt-16">
      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          <div>
            <h3 className="news-masthead text-2xl font-bold mb-4">Neural News</h3>
            <p className="text-white/60 text-sm leading-relaxed">
              Your trusted source for breaking news, in-depth analysis, and comprehensive coverage from around the world.
            </p>
          </div>
          <div>
            <h4 className="font-semibold text-sm mb-3 news-kicker text-white/90">Categories</h4>
            <div className="flex flex-col gap-2">
              {['Politics', 'Technology', 'Sports', 'Business', 'World'].map(cat => (
                <Link key={cat} to={`/category/${cat.toLowerCase()}`} className="text-sm text-white/50 hover:text-white transition-colors">
                  {cat}
                </Link>
              ))}
            </div>
          </div>
          <div>
            <h4 className="font-semibold text-sm mb-3 news-kicker text-white/90">Company</h4>
            <div className="flex flex-col gap-2">
              <Link to="/careers" className="text-sm text-white/50 hover:text-white transition-colors">Careers</Link>
              <Link to="/contact" className="text-sm text-white/50 hover:text-white transition-colors">Contact</Link>
              <Link to="/authors" className="text-sm text-white/50 hover:text-white transition-colors">Our Writers</Link>
              <Link to="/subscription" className="text-sm text-white/50 hover:text-white transition-colors">Subscribe</Link>
            </div>
          </div>
          <div>
            <h4 className="font-semibold text-sm mb-3 news-kicker text-white/90">Newsletter</h4>
            <p className="text-sm text-white/50 mb-3">Get the latest stories delivered to your inbox.</p>
            <NewsletterSignup />
          </div>
        </div>
        <div className="border-t border-white/10 mt-10 pt-6 flex flex-col sm:flex-row justify-between items-center gap-4">
          <p className="text-xs text-white/40">&copy; {new Date().getFullYear()} Neural News. All rights reserved.</p>
          <div className="flex gap-4 text-xs text-white/40">
            <Link to="/privacy" className="hover:text-white/60">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-white/60">Terms of Service</Link>
            <Link to="/contact" className="hover:text-white/60">Contact</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}

export function ArticleCard({ article, variant = 'default' }) {
  const getCategoryColor = (cat) => cat?.color || '#5B616E';
  const { headline, recordClick, isExperiment, variantId } = useHeadlineExperiment(article);
  const displayTitle = headline || article.title;
  const abAttrs = isExperiment ? { 'data-ab-variant': variantId } : {};

  if (variant === 'compact') {
    return (
      <Link to={`/article/${article.id}`} onClick={recordClick} data-testid="article-card" {...abAttrs} className="group flex gap-4 py-3">
        {article.featured_image && (
          <img
            src={article.featured_image}
            alt={displayTitle}
            className="w-20 h-20 rounded-lg object-cover flex-shrink-0"
          />
        )}
        <div className="flex-1 min-w-0">
          {article.category && (
            <span className="news-kicker text-[10px] font-semibold" style={{ color: getCategoryColor(article.category) }}>
              {article.category.name}
            </span>
          )}
          <h4 className="text-sm font-medium leading-snug mt-0.5 group-hover:underline line-clamp-2">
            {displayTitle}
          </h4>
          <span className="text-xs text-[#5B616E] mt-1 block">
            {article.created_at ? new Date(article.created_at).toLocaleDateString() : ''}
          </span>
        </div>
      </Link>
    );
  }

  if (variant === 'hero') {
    return (
      <Link to={`/article/${article.id}`} onClick={recordClick} data-testid="hero-featured-article" {...abAttrs} className="group block relative">
        <div className="aspect-[16/9] overflow-hidden rounded-xl">
          <img
            src={article.featured_image || 'https://images.unsplash.com/photo-1504711434969-e33886168d6c?w=800'}
            alt={displayTitle}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent rounded-xl" />
        </div>
        <div className="absolute bottom-0 left-0 right-0 p-6">
          {article.category && (
            <span className="breaking-label mb-2" style={{ background: getCategoryColor(article.category) }}>
              {article.category.name}
            </span>
          )}
          <h2 className="news-masthead text-2xl sm:text-3xl lg:text-4xl font-bold text-white leading-tight mt-2">
            {displayTitle}
          </h2>
          {article.subtitle && (
            <p className="text-white/80 text-sm sm:text-base mt-2 line-clamp-2">{article.subtitle}</p>
          )}
          <div className="flex items-center gap-3 mt-3 text-white/60 text-xs">
            {article.author_name && <span>{article.author_name}</span>}
            <span>{article.created_at ? new Date(article.created_at).toLocaleDateString() : ''}</span>
            <span>{formatDisplayViews(article.views, article.id)} views</span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link to={`/article/${article.id}`} onClick={recordClick} data-testid="article-card" {...abAttrs} className="news-card group block bg-white rounded-xl overflow-hidden border border-[#E7E0D6]">
      {article.featured_image && (
        <div className="aspect-[16/9] overflow-hidden">
          <img
            src={article.featured_image}
            alt={displayTitle}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        </div>
      )}
      <div className="p-4">
        {article.category && (
          <span className="news-kicker text-[10px] font-semibold" style={{ color: getCategoryColor(article.category) }}>
            {article.category.name}
          </span>
        )}
        <h3 className="news-masthead text-lg font-semibold leading-snug mt-1 group-hover:underline line-clamp-2">
          {displayTitle}
        </h3>
        {article.subtitle && (
          <p className="text-sm text-[#5B616E] mt-1.5 line-clamp-2">{article.subtitle}</p>
        )}
        <div className="flex items-center gap-3 mt-3 text-xs text-[#5B616E]">
          {article.author_name && <span>{article.author_name}</span>}
          <span>{article.created_at ? new Date(article.created_at).toLocaleDateString() : ''}</span>
          <span>{article.reading_time || Math.max(1, Math.ceil((article.content?.length || 0) / 1200))} min read</span>
        </div>
      </div>
    </Link>
  );
}
